import { useState } from "react";
import { Share2, MessageCircle, Send, Mail, Link2, Check, Facebook } from "lucide-react";
import { C } from "./theme.js";

/* ============================================================================
   הזמנת בעלי נכסים אחרים לאפליקציה.

   הקישור הוא שורש האתר — מי שנוחת שם רואה את מסך ההרשמה. ה-URL נלקח מהדפדפן
   ולא מוגדר בקוד, כך שאותו רכיב עובד מקומית, בדרופלט ובכל דומיין עתידי.

   אינסטגרם ופייסבוק-מסנג'ר אינם מקבלים טקסט בקישור חיצוני. בטלפון "עוד…"
   פותח את גיליון השיתוף של מערכת ההפעלה, ושם הם מופיעים כמו כל אפליקציה;
   במחשב נשאר "העתקת קישור" — מדביקים בסטורי או בהודעה.
   ============================================================================ */

/* וואטסאפ/טלגרם/פייסבוק שומרים קאש של תמונת התצוגה המקדימה (og:image) לפי
   מחרוזת ה-URL המדויקת ששותפה — לא לפי תוכן הקובץ בפועל. בלי פרמטר הגרסה
   כאן, שינוי עתידי ב-og.png לא ייראה בקישורים ששותפו כבר. בכל פעם שמעדכנים
   את web/public/og.png יש להעלות את המספר, כדי שהאפליקציות יביאו תצוגה טרייה
   במקום הישנה שנשמרה אצלן. */
const OG_VERSION = 2;

export const APP_URL = () => `${window.location.origin}/?og=${OG_VERSION}`;

export const INVITE_TEXT = () =>
  "ניהול נכסים — אפליקציה לבעלי דירות להשכרה עם עוזר AI: מנהלים הכול " +
  "בשיחה — גבייה ומעקב חובות, חוזים עם חתימה דיגיטלית, קריאת מונה מתמונה " +
  "ואזור אישי לדייר. נרשמים כאן: ";

const btn = (bg, color = "#fff") => ({
  background: bg,
  color,
  border: bg === "transparent" ? `1px solid ${C.line}` : "none",
});

export function ShareAppPanel({ compact = false }) {
  const [copied, setCopied] = useState(false);
  const url = APP_URL();
  const text = INVITE_TEXT();
  const full = text + url;
  const enc = encodeURIComponent;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // דפדפנים ישנים / מצב פרטי: בחירה ידנית מהשדה למטה
      const el = document.getElementById("share-app-url");
      el?.select?.();
    }
  };

  const native = async () => {
    try {
      await navigator.share({ title: "ניהול נכסים", text, url });
    } catch {
      /* המשתמש סגר את הגיליון — לא שגיאה */
    }
  };

  const links = [
    {
      key: "wa",
      label: "וואטסאפ",
      icon: <MessageCircle size={15} />,
      href: `https://wa.me/?text=${enc(full)}`,
      style: btn("#25D366"),
    },
    {
      key: "fb",
      label: "פייסבוק",
      icon: <Facebook size={15} />,
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
      style: btn("#1877F2"),
    },
    {
      key: "tg",
      label: "טלגרם",
      icon: <Send size={15} />,
      href: `https://t.me/share/url?url=${enc(url)}&text=${enc(text)}`,
      style: btn("#229ED9"),
    },
    {
      key: "mail",
      label: "אימייל",
      icon: <Mail size={15} />,
      href: `mailto:?subject=${enc("הזמנה לאפליקציית ניהול נכסים")}&body=${enc(full)}`,
      style: btn("transparent", C.ink),
    },
  ];

  return (
    <div>
      {!compact && (
        <p className="text-xs mb-3" style={{ color: C.muted }}>
          שלחו את הקישור לבעלי נכסים אחרים — הם נרשמים בעצמם ומקבלים חשבון נפרד
          משלהם. אינסטגרם ומסנג׳ר: בטלפון דרך «עוד…», במחשב מעתיקים ומדביקים.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {links.map((l) => (
          <a
            key={l.key}
            href={l.href}
            target="_blank"
            rel="noreferrer"
            className="text-sm rounded-lg px-3 py-2 flex items-center gap-1.5"
            style={l.style}
          >
            {l.icon} {l.label}
          </a>
        ))}
        <button
          onClick={copy}
          className="text-sm rounded-lg px-3 py-2 flex items-center gap-1.5"
          style={btn("transparent", copied ? C.paid : C.ink)}
        >
          {copied ? <Check size={15} /> : <Link2 size={15} />}
          {copied ? "הועתק" : "העתקת קישור"}
        </button>
        {typeof navigator !== "undefined" && navigator.share && (
          <button
            onClick={native}
            className="text-sm rounded-lg px-3 py-2 flex items-center gap-1.5"
            style={btn(C.teal)}
          >
            <Share2 size={15} /> עוד…
          </button>
        )}
      </div>

      <input
        id="share-app-url"
        readOnly
        value={url}
        onFocus={(e) => e.target.select()}
        dir="ltr"
        className="mt-3 w-full rounded-lg px-3 py-2 text-xs"
        style={{ border: `1px solid ${C.line}`, background: C.surface, color: C.muted }}
      />
    </div>
  );
}
